import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { Analytics } from '../analytics';
import {
    getBookingCalendarMode,
    getSiteCTAs,
    isInchatBookingEnabled,
} from '../config/SiteConfigManager';
import type { CTA } from '../config/types';
import { useChatLocale } from '../context/ChatThemeContext';
import {
    getCTAData,
    getCTAOverridesVersion,
    subscribeToCTAOverrides,
} from '../utils/content/ctaReplacer';
import { logger } from '../utils/core/logger';
import { getSlideUpRevealStyles } from '../utils/ui/animationUtils';
import { tryOpenBookingPopup } from '../utils/web/bookingPopup';
import { appendCtaAttributionParams } from '../utils/web/ctaUrlAttribution';
import { appendEmailToUrl } from '../utils/web/urlUtils';
import ArrowRightIcon from './ArrowRightIcon';

type SearchBarCTAButtonVariant = 'cta-primary' | 'cta-secondary';

const SEARCH_BAR_CTA_BUTTON_VARIANTS: Record<
    SearchBarCTAButtonVariant,
    {
        background: string;
        text: string;
        icon: string;
        borderRadius: string;
        fontFamily: string;
        fontWeight: string;
        lineHeight: string;
        letterSpacing: string;
    }
> = {
    'cta-primary': {
        background: 'var(--rw-bg-action-primary)',
        text: 'var(--rw-text-on-action-primary)',
        icon: 'var(--rw-text-on-action-primary)',
        borderRadius: 'var(--rw-radius-cta)',
        fontFamily: 'var(--rw-font-family-cta)',
        fontWeight: 'var(--rw-font-weight-cta-primary)',
        lineHeight: 'var(--rw-line-height-cta)',
        letterSpacing: 'var(--rw-letter-spacing-cta)',
    },
    'cta-secondary': {
        background: 'var(--rw-surface-secondary)',
        text: 'var(--rw-text-primary)',
        icon: 'var(--rw-text-primary)',
        borderRadius: 'var(--rw-radius-cta)',
        fontFamily: 'var(--rw-font-family-cta)',
        fontWeight: 'var(--rw-font-weight-cta-secondary)',
        lineHeight: 'var(--rw-line-height-cta)',
        letterSpacing: 'var(--rw-letter-spacing-cta)',
    },
};

/**
 * Hook to subscribe to CTA override changes
 * Re-renders the component when CTA overrides are updated
 */
function useCTAOverridesVersion(): number {
    return useSyncExternalStore(
        subscribeToCTAOverrides,
        getCTAOverridesVersion,
        getCTAOverridesVersion
    );
}

interface SearchBarCTAButtonProps {
    domain: string;
    placeholder: string;
    show?: boolean;
    variant?: SearchBarCTAButtonVariant;
    showArrow?: boolean;
    fontWeight?: 'font-normal' | 'font-medium' | 'font-semibold' | 'font-bold';
    placement?: 'search_bar' | 'inline';
    isCtaPage?: boolean;
    hadDemoOffer?: boolean;
    inlineCtaOptionEnabled?: boolean;
    visitorEmail?: string | null;
    onBookingFlowStart?: (
        ctaId: string,
        redirectUrl: string,
        bookingMessage: string,
        ctaAction?: 'navigate' | 'popup' | 'in-chat'
    ) => void;
    onMinimize?: () => void;
}

const SearchBarCTAButton: React.FC<SearchBarCTAButtonProps> = ({
    domain,
    placeholder,
    show = true,
    variant = 'cta-primary',
    showArrow = true,
    fontWeight,
    placement = 'search_bar',
    isCtaPage,
    hadDemoOffer,
    inlineCtaOptionEnabled,
    visitorEmail,
    onBookingFlowStart,
    onMinimize,
}) => {
    // Local flag only for one-time shimmer after first reveal
    const [hasShownOnce, setHasShownOnce] = useState(false);
    const [showShimmer, setShowShimmer] = useState(false);
    const [isRevealed, setIsRevealed] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const userLocale = useChatLocale();

    // Subscribe to CTA override changes - re-renders when overrides are updated
    const overridesVersion = useCTAOverridesVersion();

    // Determine font weight for CTAs: first CTA (with arrow) should be bold, others normal
    const effectiveFontWeight = fontWeight || (showArrow ? 'font-bold' : 'font-normal');

    // biome-ignore lint/correctness/useExhaustiveDependencies: overridesVersion is an intentional trigger — invalidates memo when CTA overrides change in the module-level store
    const ctaData = useMemo(() => {
        const ctas = getSiteCTAs(domain) as CTA[];
        const targetCTA = ctas.find((cta) => cta.placeholder === placeholder);
        const ctaDataResult = getCTAData(domain, placeholder, userLocale);

        // Booking mode is derived, not stored on the CTA: the provider (Cal.com /
        // Calendly) is detected from the CTA URL, then its integration config
        // decides 'in-chat' vs 'popup'. Null → navigate normally.
        const bookingMode = getBookingCalendarMode(domain, ctaDataResult?.url);

        return {
            ctas,
            userLanguage: userLocale,
            targetCTA,
            url: ctaDataResult?.url || '',
            text: ctaDataResult?.text || '',
            ctaId: ctaDataResult?.ctaId || targetCTA?.cta_id,
            action: (bookingMode ?? 'navigate') as 'navigate' | 'popup' | 'in-chat',
            isValid: Boolean(targetCTA && ctaDataResult?.url && ctaDataResult?.text),
        };
    }, [domain, placeholder, userLocale, overridesVersion]);

    // Track first reveal (driven by parent via `show`)
    useEffect(() => {
        if (show && ctaData.isValid && !hasShownOnce) {
            setHasShownOnce(true);
        }
    }, [show, ctaData.isValid, hasShownOnce]);

    useEffect(() => {
        if (show && ctaData.isValid) {
            const id = requestAnimationFrame(() => {
                void containerRef.current?.offsetHeight;
                setIsRevealed(true);
            });
            return () => cancelAnimationFrame(id);
        }

        setIsRevealed(false);
    }, [show, ctaData.isValid]);

    useEffect(() => {
        if (hasShownOnce) {
            const timer = setTimeout(() => {
                setShowShimmer(true);
                setTimeout(() => setShowShimmer(false), 800);
            }, 600);
            return () => clearTimeout(timer);
        }

        setShowShimmer(false);
    }, [hasShownOnce]);

    // Track CTA rendered (only once per unique CTA)
    const [hasTrackedRendered, setHasTrackedRendered] = useState(false);

    useEffect(() => {
        if (hasShownOnce && ctaData.isValid && !hasTrackedRendered) {
            // For CTAs, track once per component instance
            Analytics.trackCTARendered({
                language: ctaData.userLanguage,
                url: ctaData.url,
                label: ctaData.text,
                placement,
                isCtaPage,
                hadDemoOffer,
                inlineCtaOptionEnabled,
            });
            setHasTrackedRendered(true);
        }
    }, [
        hasShownOnce,
        ctaData.isValid,
        hasTrackedRendered,
        ctaData.userLanguage,
        ctaData.url,
        ctaData.text,
        placement,
        isCtaPage,
        hadDemoOffer,
        inlineCtaOptionEnabled,
    ]);

    const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
        const startTime = Date.now();
        logger.debug('🖱️ SearchBarCTAButton clicked', {
            placeholder,
            url: ctaData.url,
            text: ctaData.text,
            hasVisitorEmail: Boolean(visitorEmail),
        });

        const buttonElement = event.currentTarget;

        // Helper to restore UI state
        const restoreUI = () => {
            document.body.style.cursor = '';
            buttonElement.style.animation = '';
            buttonElement.style.pointerEvents = '';
        };

        // Helper to build final URL with session ID and optionally email
        const buildFinalUrl = (baseUrl: string, email?: string | null): string => {
            let finalUrl = appendCtaAttributionParams(baseUrl, domain);

            // Append email if provided
            if (email) {
                finalUrl = appendEmailToUrl(finalUrl, email);
            }

            return finalUrl;
        };

        // Helper to navigate
        const navigate = (url: string) => {
            logger.debug(`🚀 Navigating to: ${url}`);
            window.location.href = url;
        };

        // Check if the target URL points to the current page (same origin + pathname).
        // Ignores query params (UTMs differ) and normalizes trailing slashes.
        const isSamePageUrl = (url: string): boolean => {
            try {
                const target = new URL(url);
                const normalize = (p: string) => p.replace(/\/+$/, '');
                return (
                    target.origin === window.location.origin &&
                    normalize(target.pathname) === normalize(window.location.pathname)
                );
            } catch {
                return false;
            }
        };

        // Minimize widget and scroll to anchor if present in the URL.
        // Used when CTA points to the current page — avoids a full page reload
        // that would just cover the form with the re-opened widget.
        const minimizeAndScrollToAnchor = (url: string) => {
            logger.debug('🔽 CTA points to current page, minimizing widget to button');
            onMinimize?.();
            const hash = new URL(url).hash;
            if (hash) {
                // Wait for the minimize-out animation (300ms) to complete and
                // the panel to be removed from the DOM, then give the browser
                // one frame to recalculate layout before scrolling.
                setTimeout(() => {
                    requestAnimationFrame(() => {
                        document
                            .querySelector(hash)
                            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    });
                }, 350);
            }
        };

        // =================================================================
        // BOOKING FLOW: Always show confirmation message before redirect
        // =================================================================
        // If booking message is configured, ALWAYS start booking flow.
        // - If email is NOT known: bot will ask for email, then redirect
        // - If email IS known: bot will show confirmation message, then redirect
        // This ensures users always see a message before being redirected.
        const hasValidEmail =
            visitorEmail && visitorEmail !== 'unknown' && visitorEmail.includes('@');
        const ctaId = ctaData.ctaId || placeholder;

        // When in-chat booking is enabled for a domain, ALL CTAs trigger the booking flow
        // (email collection before redirect). The cta_id is optional - falls back to placeholder.
        const effectiveBookingMessage = ctaData.text;
        const inchatBookingEnabled = isInchatBookingEnabled(domain);

        logger.debug('🔧 Booking flow decision:', {
            domain,
            ctaId,
            inchatBookingEnabled,
            hasOnBookingFlowStart: Boolean(onBookingFlowStart),
        });

        // Intent-gated CTAs (e.g. a help-center link) are not sales CTAs: they navigate directly.
        const isIntentGated = Boolean(ctaData.targetCTA?.show_on_intents?.length);

        if (inchatBookingEnabled && onBookingFlowStart && !isIntentGated) {
            logger.debug('📧 Starting booking flow', {
                ctaId,
                bookingMessage: effectiveBookingMessage,
                hasValidEmail,
            });

            // Track CTA click (booking flow variant)
            const trackingData = {
                language: ctaData.userLanguage,
                url: ctaData.url,
                label: ctaData.text,
                ctaId: ctaData.targetCTA?.cta_id,
                placement,
                isCtaPage,
                hadDemoOffer,
                inlineCtaOptionEnabled,
                bookingFlowStarted: true,
                emailAlreadyKnown: Boolean(hasValidEmail),
            };
            Analytics.trackCTAClicked(trackingData).catch((error) => {
                logger.warn('Failed to track CTA click (booking flow):', error);
            });

            // Start booking flow - don't navigate yet
            // Build URL with UTM params now; email will be appended later when booking completes
            const redirectUrl = buildFinalUrl(ctaData.url);
            onBookingFlowStart(
                ctaId,
                redirectUrl,
                effectiveBookingMessage,
                ctaData.action === 'popup'
                    ? 'popup'
                    : ctaData.action === 'in-chat'
                      ? 'in-chat'
                      : undefined
            );
            return;
        }

        // =================================================================
        // NORMAL FLOW: No cta_id configured (not a booking CTA)
        // =================================================================
        // Show loading feedback: cursor, animation, and disable clicks
        document.body.style.cursor = 'wait';
        buttonElement.style.animation = 'breathe 0.6s ease-in-out infinite';
        buttonElement.style.pointerEvents = 'none';

        const trackingData: {
            language: string;
            url: string;
            label: string;
            ctaId?: string;
            placement: 'search_bar' | 'inline';
            isCtaPage?: boolean;
            hadDemoOffer?: boolean;
            inlineCtaOptionEnabled?: boolean;
            emailPreFilled?: boolean;
        } = {
            language: ctaData.userLanguage,
            url: ctaData.url,
            label: ctaData.text,
            placement,
            isCtaPage,
            hadDemoOffer,
            inlineCtaOptionEnabled,
        };

        // Only include ctaId if it exists in the config
        if (ctaData.targetCTA?.cta_id) {
            trackingData.ctaId = ctaData.targetCTA.cta_id;
        }

        // Track if email was pre-filled
        if (hasValidEmail) {
            trackingData.emailPreFilled = true;
        }

        try {
            // Track CTA click with dual-send strategy (standard + sendBeacon)
            // The trackEventRobustly function includes a 400ms delay to allow
            // PostHog time to send events before navigation occurs
            logger.debug('📊 Tracking CTA click...');
            await Analytics.trackCTAClicked(trackingData);

            const trackingDuration = Date.now() - startTime;
            logger.debug(`✅ Tracking completed in ${trackingDuration}ms, navigating...`);
        } catch (error) {
            // Log tracking errors but don't block navigation
            const trackingDuration = Date.now() - startTime;
            logger.warn(`⚠️ Tracking failed after ${trackingDuration}ms, navigating anyway:`, error);
        } finally {
            // Always restore UI and act, regardless of tracking success
            const finalUrl = buildFinalUrl(ctaData.url, hasValidEmail ? visitorEmail : null);
            restoreUI();

            if (ctaData.action === 'popup' && tryOpenBookingPopup(finalUrl)) {
                logger.debug('📅 CTA opened as popup');
            } else if (isSamePageUrl(finalUrl) && onMinimize) {
                minimizeAndScrollToAnchor(finalUrl);
            } else {
                navigate(finalUrl);
            }
        }
    };

    if (!ctaData.isValid) {
        return null;
    }

    const variantStyles = SEARCH_BAR_CTA_BUTTON_VARIANTS[variant];
    const revealStyles = getSlideUpRevealStyles({ show: isRevealed, transitionDelay: '0s' });

    return (
        <div
            ref={containerRef}
            className="inboundx-cta-container"
            style={{
                ...revealStyles,
                transform: isRevealed ? 'none' : revealStyles.transform,
                willChange: isRevealed ? 'auto' : 'transform, opacity',
                visibility: isRevealed ? 'visible' : 'hidden',
                pointerEvents: show ? 'auto' : 'none',
            }}
        >
            <button
                type="button"
                className="inboundx-search-bar-cta-button flex items-center px-3 py-[7px] rounded-10 border-none outline-none no-underline transition-all duration-200 ease-in-out relative overflow-hidden cursor-pointer hover:brightness-115 hover:opacity-90 active:brightness-75 active:scale-90 max-sm:text-sm max-sm:px-[10px] max-sm:py-1 max-sm:h-[27px]"
                onClick={handleClick}
                style={{
                    background: variantStyles.background,
                    borderRadius: variantStyles.borderRadius,
                    fontFamily: variantStyles.fontFamily,
                }}
            >
                {showShimmer && (
                    <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background:
                                'linear-gradient(135deg, transparent 30%, rgba(255,255,255,0.25) 50%, transparent 80%)',
                            transform: 'translateX(-100%) translateY(-100%)',
                            animation: 'rw-diagonal-shimmer 0.8s ease-out forwards',
                            width: '200%',
                            height: '200%',
                        }}
                    />
                )}

                <span
                    className={`${effectiveFontWeight} leading-tight relative z-10`}
                    style={{
                        color: variantStyles.text,
                        fontWeight: variantStyles.fontWeight,
                        lineHeight: variantStyles.lineHeight,
                        letterSpacing: variantStyles.letterSpacing,
                    }}
                >
                    {ctaData.text}
                </span>
                {showArrow && (
                    <ArrowRightIcon
                        width={20}
                        height={20}
                        color={variantStyles.icon}
                        className="flex-none relative z-10"
                    />
                )}

                <style>{`
                    @keyframes breathe {
                        0%, 100% {
                            opacity: 1;
                        }
                        50% {
                            opacity: 0.7;
                        }
                    }
                `}</style>
            </button>
        </div>
    );
};

export default SearchBarCTAButton;
